import { useState, useRef, useEffect, useCallback } from "react";
import { Keyboard, Camera, Printer, RotateCcw, Barcode } from "lucide-react";
import { useTranslation } from "../i18n";
import useCameraBarcode from "../hooks/useCameraBarcode";
import useTntOcr from "../hooks/useTntOcr";
import { recordScanAnomaly } from "../api/scanTelemetry";
import { noteScan } from "./gamification/scanGuard";
import { useReadOnly } from "../access/ReadOnly";
import { playError } from "./helpers/audioPlayer";
import "../styles/hidden-scan-input.css";

interface HiddenScanInputProps {
    value: string;
    onChange: (val: string) => void;
    onSubmit: (scannedValue?: string) => void;
    disabled?: boolean;
    /** Explicit read-only override — same effect as a page being wrapped in
     *  <ReadOnlyProvider value={true}> (ACCESS_CONTROL_PLAN.md §8.4), which
     *  this component also consults directly via useReadOnly() so pages
     *  under a read-only route don't have to thread it through by hand.
     *  Distinct from `disabled`: `disabled` HOLDS a completed scan and
     *  replays it once re-enabled (network round trips, modal transitions);
     *  read-only REJECTS the scan outright with an error tone — nothing is
     *  held, because there's nothing this page is going to do with it. */
    readOnly?: boolean;
    /** Lookup-only pages (SKU Check): a scan reads, it never mutates, so the page's read grant must
     *  still let the operator scan. Ignores the read-only CONTEXT for scan gating; the explicit
     *  `readOnly` prop still wins. (review 2026-09-24 AC-05) */
    allowReadOnlyScans?: boolean;
    placeholder?: string;
    printConnected?: boolean;
    printFlash?: boolean;
    onPrintClick?: () => void;
    onReprintClick?: () => void;
    canReprint?: boolean;
    /** Reprint-mode toggle rendered in the left cluster next to the print
     *  button (green when on). While on, the page treats every scan as a
     *  "reprint the original barcode" request instead of its normal flow. */
    reprintMode?: boolean;
    onReprintModeToggle?: () => void;
    /** Enable the TNT OCR mode toggle on the camera button (off → barcode → TNT → off).
     *  When false (default), camera button just toggles barcode scanning on/off. */
    enableTntMode?: boolean;
    /** Hide the camera button entirely. Use on pages that only need hardware
     *  scanner / keyboard input (e.g. dispatch). Defaults to false. */
    disableCamera?: boolean;
    /** Hide the entire keyboard/camera toggle row. Used on picking-flow
     *  pages (Pick & Sort / Single / Large picking, Shelf Drop, Forecast
     *  Drop, drop put-away) where HID scanners are the only supported
     *  input — the toggles would just be visual noise. */
    hideToggles?: boolean;
    /** Notifies the parent when the user toggles the on-screen keyboard open/closed.
     *  Fires whether the toggle happens via the button or the scanner-burst
     *  auto-close path. */
    onKeyboardToggle?: (open: boolean) => void;
    /** Fires when the next submission was detected to be a hardware scanner
     *  or paste while the on-screen keyboard was open. Parents can use this
     *  to void any keyboard-mode time bonus for the upcoming scan. */
    onSuspectScan?: () => void;
    /** Optional extra control stacked above the keyboard/camera toggles in
     *  the right-hand cluster (.scan-toggle-right), so it tracks the keyboard
     *  state instead of overlapping the manual input field. */
    extraToggle?: React.ReactNode;
    /** Optional content rendered centred in the gap between the print cluster
     *  (left) and the keyboard/camera cluster (right). Takes the free space, so
     *  it never overlaps the side buttons. */
    centreContent?: React.ReactNode;
    /** Move the keyboard toggle into the LEFT cluster (next to the print
     *  icon) instead of the right. Frees the bottom-right corner for a page's
     *  own fixed UI (e.g. the Wipe stats bar). Defaults to false. */
    keyboardOnLeft?: boolean;
    /** Names this screen in scan-telemetry rows so a dropped scan lands in the
     *  Logs page under the same department as the scans around it. Defaults to
     *  "Client". */
    telemetryModule?: string;
}

// Hardware scanners outpace the UI: an operator can start the next scan
// while the previous submission's network round trip is still in flight
// (input `disabled`) or while one scan screen is unmounting and the next
// is mounting. A per-instance buffer loses those keystrokes — the scan
// either vanishes silently or arrives truncated ("item not in this
// tote", works on retry — 2026-08-06 dispatch go-live). So the burst
// buffer and any scan completed while no enabled instance could accept
// it live at MODULE scope: the next enabled instance replays the held
// scan the moment it's ready. The hold expires quickly — a scan parked
// longer than a screen transition must not fire into an unrelated page.
let sharedBuffer = "";
// Wall-clock of the last buffered keystroke. Scanner keystrokes arrive
// milliseconds apart; a longer gap means the buffered prefix is a stale
// leftover (abandoned partial scan, stray keypress before an unmount
// cancelled the flush timer) and must not contaminate the next scan.
let sharedBufferLastKeyAt = 0;
const BUFFER_STALE_MS = 500;
// Adaptive flush for scanners with no Enter suffix: once a burst is
// clearly machine-speed (several keys, tiny gaps), the scan is complete
// ~FAST_FLUSH_MS after the last key instead of the conservative 500ms —
// suffix-less scanners feel ~4x snappier. Human-cadence input keeps the
// long window.
let sharedBurstKeyCount = 0;
let sharedBurstStartedAt = 0;
const FAST_FLUSH_MS = 120;
const SCANNER_AVG_GAP_MS = 50;
let heldScan: { value: string; at: number } | null = null;
const HELD_SCAN_TTL_MS = 5000;

// ── Swallowed-scan detector ───────────────────────────────────────
// The two early returns in handleKeyDown (on-screen keyboard open, or focus
// sitting in a field) drop scanner keystrokes BEFORE they reach the buffer.
// Nothing is held, nothing is submitted, and the operator gets no feedback —
// the scan simply never happened, which on the floor is indistinguishable
// from a slow light. We can't recover those keystrokes, but we can recognise
// their cadence: only a scanner produces 4+ printable keys averaging under
// SCANNER_AVG_GAP_MS apart. Report once per burst.
let swallowedKeys = 0;
let swallowedStartedAt = 0;
let swallowedLastKeyAt = 0;
let swallowedReported = false;

const noteSwallowedKey = (reason: string, module: string, detail: Record<string, unknown>) => {
    const now = Date.now();
    // Same staleness rule as the real buffer: a long gap means this key
    // belongs to a new burst, not the one before it.
    if (now - swallowedLastKeyAt > BUFFER_STALE_MS) {
        swallowedKeys = 0;
        swallowedStartedAt = now;
        swallowedReported = false;
    }
    swallowedLastKeyAt = now;
    swallowedKeys += 1;
    if (swallowedReported || swallowedKeys < 4) return;
    const avgGap = (now - swallowedStartedAt) / (swallowedKeys - 1);
    if (avgGap >= SCANNER_AVG_GAP_MS) return;   // human typing, not a scanner
    swallowedReported = true;
    recordScanAnomaly("swallowed", module, { reason, keys: swallowedKeys, avg_gap_ms: Math.round(avgGap), ...detail });
};

/** Test-only: clear the module-scoped scan state between test cases. */
export const __resetScanBufferForTests = () => {
    sharedBuffer = "";
    sharedBufferLastKeyAt = 0;
    sharedBurstKeyCount = 0;
    sharedBurstStartedAt = 0;
    heldScan = null;
    swallowedKeys = 0;
    swallowedStartedAt = 0;
    swallowedLastKeyAt = 0;
    swallowedReported = false;
};

const HiddenScanInput = ({ value, onChange, onSubmit, disabled = false, readOnly = false, allowReadOnlyScans = false, placeholder = "", printConnected, printFlash, onPrintClick, onReprintClick, canReprint, reprintMode = false, onReprintModeToggle, enableTntMode = false, disableCamera = false, hideToggles = false, onKeyboardToggle, onSuspectScan, extraToggle, centreContent, keyboardOnLeft = false, telemetryModule = "Client" }: HiddenScanInputProps) => {
    const { t } = useTranslation();
    const contextReadOnly = useReadOnly();
    const isReadOnly = readOnly || (contextReadOnly && !allowReadOnlyScans);
    const [showKeyboard, setShowKeyboard] = useState(false);
    // Camera always starts off — user must explicitly enable each session.
    const [cameraMode, setCameraMode] = useState<"off" | "barcode" | "tnt">("off");
    const manualRef = useRef<HTMLInputElement>(null);
    const showKeyboardRef = useRef(false);
    const cameraActiveRef = useRef(false);
    const disabledRef = useRef(disabled);
    const isReadOnlyRef = useRef(isReadOnly);
    const onSubmitRef = useRef(onSubmit);
    const onChangeRef = useRef(onChange);
    const onKeyboardToggleRef = useRef(onKeyboardToggle);
    const onSuspectScanRef = useRef(onSuspectScan);
    const telemetryModuleRef = useRef(telemetryModule);
    const keystrokeTimestampsRef = useRef<number[]>([]);
    const keystrokeCountRef = useRef(0);
    const firstKeystrokeAtRef = useRef<number | null>(null);

    // Keep refs in sync
    useEffect(() => { showKeyboardRef.current = showKeyboard; }, [showKeyboard]);
    useEffect(() => { disabledRef.current = disabled; }, [disabled]);
    useEffect(() => { isReadOnlyRef.current = isReadOnly; }, [isReadOnly]);
    useEffect(() => { telemetryModuleRef.current = telemetryModule; }, [telemetryModule]);
    useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
    useEffect(() => { onKeyboardToggleRef.current = onKeyboardToggle; }, [onKeyboardToggle]);
    useEffect(() => { onSuspectScanRef.current = onSuspectScan; }, [onSuspectScan]);

    // Camera barcode scanning
    const handleCameraScan = useCallback((scannedValue: string) => {
        if (isReadOnlyRef.current) {
            playError();
            return;
        }
        noteScan(scannedValue);
        onChange(scannedValue);
        setTimeout(() => onSubmit(scannedValue), 0);
    }, [onChange, onSubmit]);

    const { isCameraActive, isCameraAvailable, cameraError, toggleCamera, videoRef } =
        useCameraBarcode({
            onScan: handleCameraScan,
            enabled: !disabled && !isReadOnly && !disableCamera && cameraMode !== "tnt",
            zoom: 2,
            torch: cameraMode === "tnt",
        });

    // TNT OCR — only active when user explicitly selects TNT mode
    const { lastRead } = useTntOcr({
        videoRef,
        onScan: handleCameraScan,
        active: isCameraActive && cameraMode === "tnt" && !disabled && !isReadOnly,
    });

    useEffect(() => { cameraActiveRef.current = isCameraActive; }, [isCameraActive]);

    // Sync cameraMode when camera starts/stops externally (auto-start from
    // localStorage, permission revoked, track ended, etc.)
    const wasCameraActiveRef = useRef(false);
    useEffect(() => {
        if (isCameraActive) {
            wasCameraActiveRef.current = true;
            // Camera auto-started but mode is still "off" — sync to barcode
            if (cameraMode === "off") {
                setCameraMode("barcode");
            }
        } else if (wasCameraActiveRef.current) {
            wasCameraActiveRef.current = false;
            setCameraMode("off");
        }
    }, [isCameraActive, cameraMode]);

    // Camera icon click — uses isCameraActive as the source of truth so state
    // drift (e.g. during async camera startup) can't lock the user out of TNT mode.
    const handleCameraClick = useCallback(() => {
        if (!isCameraActive) {
            // Camera off → start in barcode mode
            toggleCamera();
            setCameraMode("barcode");
        } else if (cameraMode === "tnt") {
            // TNT mode → turn off
            toggleCamera();
            setCameraMode("off");
        } else if (enableTntMode) {
            // Camera active → switch to TNT (only when TNT mode is enabled)
            setCameraMode("tnt");
        } else {
            // No TNT mode — just toggle camera off
            toggleCamera();
            setCameraMode("off");
        }
    }, [isCameraActive, cameraMode, toggleCamera, enableTntMode]);

    // Refocus manual input when value is cleared after a submission
    useEffect(() => {
        if (showKeyboard && value === "" && !disabled) {
            const t = setTimeout(() => manualRef.current?.focus(), 50);
            return () => clearTimeout(t);
        }
    }, [showKeyboard, value, disabled]);

    // Global keydown listener — captures hardware scanner keystrokes.
    // Works regardless of whether the camera is active — hardware scanners
    // emit keystrokes which are independent of the camera's barcode detection.
    useEffect(() => {
        let timeout: NodeJS.Timeout | null = null;

        // A scan that completes while this instance can't accept it
        // (disabled during a network round trip, or mid unmount/mount)
        // is HELD rather than dropped; the replay effect below fires it
        // as soon as an enabled instance is ready.
        const completeScan = (val: string) => {
            sharedBuffer = "";
            if (isReadOnlyRef.current) {
                // Read-only: nothing to hold — the page can't act on this
                // scan now or later, so reject it outright with an error
                // tone rather than parking it for a replay that would land
                // on a page the operator may have since left.
                playError();
                return;
            }
            if (disabledRef.current) {
                heldScan = { value: val, at: Date.now() };
                recordScanAnomaly("held", telemetryModuleRef.current,
                    { reason: "input_disabled" }, { barcode: val });
                return;
            }
            noteScan(val);
            onChangeRef.current(val);
            onSubmitRef.current(val);
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (showKeyboardRef.current) {
                // Scanner aimed at the page while the on-screen keyboard owns
                // input: the manual field takes it only if it happens to be
                // focused, otherwise the scan is gone.
                if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
                    noteSwallowedKey("keyboard_open", telemetryModuleRef.current, {
                        focused_tag: (e.target as HTMLElement)?.tagName ?? null,
                    });
                }
                return;
            }

            const target = e.target as HTMLElement;
            const tag = target.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
                // Focus is parked in a field, so these keystrokes never reach
                // the shared buffer and no request is ever made. This is the
                // silent-drop path — record it, because nothing else can.
                if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
                    noteSwallowedKey("focus_in_field", telemetryModuleRef.current, {
                        focused_tag: tag,
                        focused_id: target.id || null,
                        focused_class: typeof target.className === "string" ? target.className.slice(0, 80) : null,
                    });
                }
                return;
            }
            // Buttons keep focus after a tap (notably on Android/Chrome), and
            // ignoring their keystrokes created a scan dead zone: tap any
            // button, then every scan was silently dropped. Instead, keep
            // buffering scanner keystrokes while a button is focused —
            // preventDefault stops Space/Enter from "clicking" the button
            // mid-scan. A bare Enter with nothing buffered still falls
            // through so keyboard activation of buttons keeps working.
            const onButton = tag === "BUTTON" || !!target.closest?.("button");

            if (e.ctrlKey || e.altKey || e.metaKey) return;

            if (e.key === "Enter") {
                if (timeout) { clearTimeout(timeout); timeout = null; }
                const val = sharedBuffer.trim();
                if (val) {
                    e.preventDefault();
                    completeScan(val);
                }
                return;
            }

            if (e.key.length === 1) {
                if (onButton) e.preventDefault();
                // Self-heal a stale prefix: scanner bursts are ms apart, so
                // a long gap means whatever is buffered belongs to an
                // earlier, abandoned entry.
                const now = Date.now();
                if (now - sharedBufferLastKeyAt > BUFFER_STALE_MS) {
                    sharedBuffer = "";
                    sharedBurstKeyCount = 0;
                    sharedBurstStartedAt = now;
                }
                sharedBufferLastKeyAt = now;
                sharedBurstKeyCount += 1;
                sharedBuffer += e.key;
                if (!disabledRef.current) onChangeRef.current(sharedBuffer);

                // Machine-cadence burst (≥4 keys averaging under 50ms/key)
                // → the scan is over almost immediately after keys stop.
                const avgGap = sharedBurstKeyCount > 1
                    ? (now - sharedBurstStartedAt) / (sharedBurstKeyCount - 1)
                    : Infinity;
                const flushMs = sharedBurstKeyCount >= 4 && avgGap < SCANNER_AVG_GAP_MS
                    ? FAST_FLUSH_MS
                    : 500;

                if (timeout) clearTimeout(timeout);
                timeout = setTimeout(() => {
                    const val = sharedBuffer.trim();
                    if (val) completeScan(val);
                }, flushMs);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            // Deliberately do NOT clear sharedBuffer — a scan straddling
            // this instance's unmount finishes into the next instance.
            if (timeout) clearTimeout(timeout);
        };
    }, []);

    // Replay a held scan the moment this instance can take it: fires on
    // mount and whenever `disabled` clears. TTL-guarded so a scan parked
    // during one flow can't fire into an unrelated screen much later.
    useEffect(() => {
        if (disabled) return;
        const held = heldScan;
        if (!held) return;
        heldScan = null;
        const waited = Date.now() - held.at;
        if (waited > HELD_SCAN_TTL_MS) {
            // The scan was parked for longer than a screen transition and is
            // now discarded. To the operator this is a scan that did nothing.
            recordScanAnomaly("held_expired", telemetryModuleRef.current,
                { ttl_ms: HELD_SCAN_TTL_MS }, { barcode: held.value, waited_ms: waited });
            return;
        }
        noteScan(held.value);
        onChangeRef.current(held.value);
        // Post-render tick — parents often reset state in the same commit
        // that re-enables scanning.
        const t = setTimeout(() => onSubmitRef.current(held.value), 0);
        return () => clearTimeout(t);
    }, [disabled]);

    const handleManualKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();

            if (isReadOnly) {
                playError();
                return;
            }

            // Read the live DOM value — a fast paste + Enter can outrun React's
            // re-render, so the `value` prop captured in this closure may
            // still be the pre-paste state.
            const submittedValue = e.currentTarget.value ?? "";

            // Final scanner check at submit-time: the +20s keyboard bonus is
            // only legitimate if the value was actually typed character by
            // character at a human cadence. Flag the entry as non-manual if:
            //   (a) fewer keystrokes were observed than characters submitted
            //       (→ paste, autofill, programmatic set) or
            //   (b) the keystrokes that *did* fire averaged faster than any
            //       human typist could manage (→ hardware scanner).
            const submittedLen = submittedValue.length;
            const count = keystrokeCountRef.current;
            const stamps = keystrokeTimestampsRef.current;

            let looksNonManual = false;
            if (submittedLen > 0 && count < submittedLen) {
                looksNonManual = true; // paste / autofill
            } else if (stamps.length >= 4) {
                const span = stamps[stamps.length - 1] - stamps[0];
                const avg = span / (stamps.length - 1);
                if (avg < 60) looksNonManual = true; // scanner cadence
            }

            if (looksNonManual) {
                keystrokeTimestampsRef.current = [];
                keystrokeCountRef.current = 0;
                firstKeystrokeAtRef.current = null;
                // setShowKeyboard(false);
                // if ("virtualKeyboard" in navigator) {
                //     (navigator as any).virtualKeyboard.hide();
                // }
                // onKeyboardToggleRef.current?.(false);
                onSuspectScanRef.current?.();
            }

            if (submittedLen > 0) {
                noteScan(submittedValue);
                onChange(submittedValue);
                onSubmit(submittedValue);
            }
            setTimeout(() => {
                manualRef.current?.focus();
            }, 50);
            return;
        }

        // Scanner-burst detection: if keystrokes arrive faster than any human
        // typist can manage, infer a hardware scanner has been aimed at the
        // manual input. Close the keyboard and submit the burst through the
        // standard path so the timer bonus for keyboard mode is revoked.
        if (e.key.length !== 1) return;
        const now = performance.now();
        keystrokeCountRef.current += 1;
        if (firstKeystrokeAtRef.current === null) firstKeystrokeAtRef.current = now;
        const stamps = keystrokeTimestampsRef.current;
        stamps.push(now);
        if (stamps.length > 6) stamps.shift();

        // if (stamps.length >= 4) {
        //     let sum = 0;
        //     for (let i = 1; i < stamps.length; i++) sum += stamps[i] - stamps[i - 1];
        //     const avg = sum / (stamps.length - 1);
        //     if (avg < 40) {
        //         const input = e.currentTarget;
        //         const burstValue = (input.value ?? "") + e.key;
        //         keystrokeTimestampsRef.current = [];
        //         keystrokeCountRef.current = 0;
        //         firstKeystrokeAtRef.current = null;
        //         e.preventDefault();
        //         // Close the keyboard (mirrors handleToggleKeyboard side-effects)
        //         // setShowKeyboard(false);
        //         // if ("virtualKeyboard" in navigator) {
        //         //     (navigator as any).virtualKeyboard.hide();
        //         // }
        //         // onKeyboardToggleRef.current?.(false);
        //         onSuspectScanRef.current?.();
        //         onChange(burstValue);
        //         setTimeout(() => onSubmit(burstValue), 0);
        //     }
        // }
    }, [onSubmit, onChange, value, isReadOnly]);

    const handleToggleKeyboard = useCallback(() => {
        setShowKeyboard(prev => {
            const next = !prev;
            keystrokeTimestampsRef.current = [];
            keystrokeCountRef.current = 0;
            firstKeystrokeAtRef.current = null;
            if (next) {
                setTimeout(() => {
                    manualRef.current?.focus();
                    // Optional-call: fires 100ms after the toggle, by which
                    // point the field may be gone (keyboard closed again,
                    // screen unmounted), and scrollIntoView isn't implemented
                    // in every context this runs in (jsdom has none). Running
                    // on a timer, a throw here would land wherever the event
                    // loop happens to be — in tests, sometimes inside an
                    // act() window, failing an unrelated assertion.
                    manualRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
                    if ("virtualKeyboard" in navigator) {
                        (navigator as any).virtualKeyboard.show();
                    }
                }, 100);
            } else {
                if ("virtualKeyboard" in navigator) {
                    (navigator as any).virtualKeyboard.hide();
                }
            }
            onKeyboardToggleRef.current?.(next);
            return next;
        });
    }, []);

    const isTntMode = cameraMode === "tnt";

    // Camera button class — green for barcode, orange for TNT
    const cameraButtonClass = `scan-input-toggle-btn scan-input-toggle-camera ${isCameraActive ? (isTntMode ? "tnt" : "active") : ""}`;

    // Keyboard toggle — rendered in either the left or right cluster.
    const keyboardBtn = (
        <button
            type="button"
            className={`scan-input-toggle-btn scan-input-toggle-keyboard ${showKeyboard ? "active" : ""}`}
            onClick={handleToggleKeyboard}
            disabled={disabled}
            aria-label="Toggle keyboard"
        >
            <Keyboard size={20} />
        </button>
    );

    // Left cluster: print status + reprint (+ keyboard when keyboardOnLeft).
    // Carries margin-right:auto so the right cluster stays pinned right; when
    // that's absent the left cluster sits at the far left.
    const hasPrintCluster = printConnected !== undefined || (canReprint && onReprintClick) || !!onReprintModeToggle || keyboardOnLeft;
    const printCluster = hasPrintCluster && (
        <div className="scan-toggle-left">
            {printConnected !== undefined && (
                <button
                    type="button"
                    className={`scan-input-toggle-btn ${printConnected ? "scan-input-toggle-print-on" : "scan-input-toggle-print-off"} ${printFlash ? "scan-input-toggle-print-flash" : ""}`}
                    onClick={onPrintClick}
                    aria-label="Print status"
                >
                    <Printer size={20} />
                </button>
            )}
            {onReprintModeToggle && (
                <div className="scan-reprint-btn-wrap">
                    {reprintMode && <span className="scan-reprint-mode-label">REPRINT</span>}
                    <button
                        type="button"
                        className={`scan-input-toggle-btn scan-input-toggle-reprint-mode ${reprintMode ? "active" : ""}`}
                        onClick={onReprintModeToggle}
                        disabled={disabled}
                        aria-label="Toggle reprint mode"
                    >
                        <Barcode size={20} />
                    </button>
                </div>
            )}
            {canReprint && onReprintClick && (
                <button
                    type="button"
                    className="scan-input-toggle-btn scan-input-toggle-reprint"
                    onClick={onReprintClick}
                    aria-label="Reprint last"
                >
                    <RotateCcw size={20} />
                </button>
            )}
            {keyboardOnLeft && keyboardBtn}
        </div>
    );

    // Right cluster: oversize (extraToggle) stacked above the camera/keyboard
    // row. Hidden when hideToggles is set, or when there's nothing to show
    // (e.g. keyboardOnLeft + camera disabled + no extraToggle).
    const showCamera = isCameraAvailable && !disableCamera;
    const showRightCluster = !hideToggles && (!!extraToggle || showCamera || !keyboardOnLeft);
    const toggleCluster = showRightCluster && (
        <div className="scan-toggle-right">
            {extraToggle && (
                <div className="scan-input-toggles-extra">{extraToggle}</div>
            )}
            <div className="scan-input-toggles-row">
                {showCamera && (
                    <div className="scan-camera-btn-wrap">
                        {isTntMode && <span className="scan-tnt-mode-label">TNT MODE</span>}
                        <button
                            type="button"
                            className={cameraButtonClass}
                            onClick={(e) => { handleCameraClick(); (e.currentTarget as HTMLButtonElement).blur(); }}
                            disabled={disabled}
                            aria-label="Toggle camera"
                        >
                            <Camera size={20} />
                        </button>
                    </div>
                )}
                {!keyboardOnLeft && keyboardBtn}
            </div>
        </div>
    );

    return (
        <div className={`scan-input-container ${showKeyboard ? "kb-open" : ""}`}>
            {/* Single toggle row: print (left) | centre | oversize+kbd+camera
                (right). Rendered once for both keyboard states — the container
                lifts above the virtual keyboard via the .kb-open modifier. */}
            <div className="scan-toggle-row">
                {printCluster}
                {centreContent && (
                    <div className="scan-toggle-centre">{centreContent}</div>
                )}
                {toggleCluster}
            </div>

            {showKeyboard && (
                <input
                    ref={manualRef}
                    className="scan-input-manual-field"
                    type="text"
                    inputMode="text"
                    // @ts-ignore — virtualkeyboardpolicy is a valid HTML attribute (Chrome 94+)
                    virtualkeyboardpolicy="manual"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleManualKeyDown}
                    onBlur={() => {
                        if (showKeyboardRef.current && !disabledRef.current) {
                            setTimeout(() => manualRef.current?.focus(), 100);
                        }
                    }}
                    placeholder={placeholder || t("scan_here")}
                    autoComplete="off"
                    disabled={disabled}
                    enterKeyHint="go"
                />
            )}

            {isCameraActive && (
                <div className="scan-viewfinder">
                    <video
                        ref={videoRef as React.RefObject<HTMLVideoElement>}
                        autoPlay
                        playsInline
                        muted
                    />
                    {!isTntMode && (
                        <>
                            <div className="scan-viewfinder-corner tl" />
                            <div className="scan-viewfinder-corner tr" />
                            <div className="scan-viewfinder-corner bl" />
                            <div className="scan-viewfinder-corner br" />
                            <div className="scan-viewfinder-laser" />
                        </>
                    )}
                    {isTntMode && (
                        <>
                            <div className="scan-tnt-badge">TNT</div>
                            <div className="scan-tnt-target">
                                <div className="scan-tnt-target-label">88XXXXXXXXXXX</div>
                            </div>
                            {lastRead && (
                                <div className="scan-tnt-readout">{lastRead}</div>
                            )}
                        </>
                    )}
                </div>
            )}

            {cameraError && !isCameraActive && (
                <div className="scan-camera-error">
                    {cameraError === "permission_denied" ? t("camera_denied") : t("camera_unavailable")}
                </div>
            )}
        </div>
    );
};

export default HiddenScanInput;
